
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Mail, AtSign, Globe, Instagram, Music, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { UserProfile } from '@/types';
import { supabase } from '@/integrations/supabase/client';

interface ProfileFormProps {
  profile: UserProfile;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ profile }) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  
  const [displayName, setDisplayName] = useState(profile.displayName || '');
  const [bio, setBio] = useState(profile.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(profile.avatarUrl || '');
  
  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      toast({
        title: "Error",
        description: "Display name cannot be empty",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: displayName,
          bio: bio,
          avatar_url: avatarUrl,
        })
        .eq('id', profile.userId);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile changes have been saved",
      });

      // Refresh the page to show updated data
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Error saving profile",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Card className="neo-card">
      <CardHeader>
        <CardTitle>Edit Profile</CardTitle>
        <CardDescription>Update your profile information</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Display Name</label>
            <Input 
              value={displayName} 
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Avatar URL</label>
            <Input 
              value={avatarUrl} 
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Bio</label>
          <Textarea 
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            placeholder="Tell us about yourself..."
          />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-1">
              <Mail className="h-4 w-4" />
              Email
            </label>
            <Input defaultValue="alex@turner-music.com" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-1">
              <AtSign className="h-4 w-4" />
              Username
            </label>
            <Input defaultValue={profile.displayName} />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Location</label>
          <Input defaultValue={profile.location} />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">Social Links</label>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center gap-2">
              <Music className="h-5 w-5 text-muted-foreground" />
              <Input defaultValue={profile.socialLinks.spotify || ''} />
            </div>
            <div className="flex items-center gap-2">
              <Instagram className="h-5 w-5 text-muted-foreground" />
              <Input defaultValue={profile.socialLinks.instagram || ''} />
            </div>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <Input defaultValue={profile.socialLinks.website || ''} />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end pt-4">
          <Button 
            className="gap-2"
            onClick={handleSaveProfile}
            disabled={isSaving}
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileForm;
